import { request, requestNoContent } from './client';

/**
 * Tầng gọi API của mốc dự án.
 *
 * <b>Ba thao tác đổi ngày là ba lời gọi riêng</b> — dời lịch, dự báo, ghi nhận đạt — đúng như ở máy
 * chủ. Gộp lại thành một phép sửa chung sẽ tạo ra đường vòng để dời lịch mà không phải khai lý do
 * trễ, và lý do trễ là thứ đáng giá nhất của cả bảng này.
 */

/** Đường dẫn gốc của dự án. */
const projectRoute = '/api/v1/projects';

/** Dựng đường dẫn đường mốc của một dự án. */
function milestoneRoute(projectId: string): string {
  return `${projectRoute}/${projectId}/milestones`;
}

/**
 * Bên chịu trách nhiệm khi một mốc bị trễ.
 *
 * Giá trị phải khớp với <c>ProjectMilestoneDelayParty</c> ở máy chủ. Đây là trường có giá trị tiền
 * bạc lớn nhất của bảng mốc: điều khoản phạt chậm tiến độ chỉ áp cho phần chậm do bên cung cấp gây
 * ra.
 */
export const ProjectMilestoneDelayParty = {
  /** Do phía mình — thiếu người, thiếu vật tư, làm sai phải làm lại. */
  Us: 1,

  /** Do khách hàng — mặt bằng chưa sẵn sàng, mẫu giao muộn, chậm duyệt thiết kế. */
  Customer: 2,

  /** Do nhà cung cấp — camera, ống kính, bộ điều khiển về muộn hơn cam kết. */
  Supplier: 3,

  /** Do nhà thầu phụ — phần cơ khí, phần điện hoặc phần lắp đặt thuê ngoài. */
  Subcontractor: 4,

  /** Bất khả kháng — thiên tai, dịch bệnh, thay đổi quy định. */
  ForceMajeure: 5,

  /** Do nhiều bên cùng lúc, không tách được phần của ai. */
  Shared: 6,
} as const;

/** Giá trị của bên chịu trách nhiệm. */
export type ProjectMilestoneDelayPartyValue =
  (typeof ProjectMilestoneDelayParty)[keyof typeof ProjectMilestoneDelayParty];

/** Tên tiếng Việt của từng bên chịu trách nhiệm. */
export const projectMilestoneDelayPartyNames: Readonly<
  Record<ProjectMilestoneDelayPartyValue, string>
> = {
  [ProjectMilestoneDelayParty.Us]: 'Do phía mình',
  [ProjectMilestoneDelayParty.Customer]: 'Do khách hàng',
  [ProjectMilestoneDelayParty.Supplier]: 'Do nhà cung cấp',
  [ProjectMilestoneDelayParty.Subcontractor]: 'Do nhà thầu phụ',
  [ProjectMilestoneDelayParty.ForceMajeure]: 'Bất khả kháng',
  [ProjectMilestoneDelayParty.Shared]: 'Do nhiều bên',
};

/** Một mốc dự án. */
export interface ProjectMilestone {
  readonly id: string;
  readonly projectId: string;
  readonly sequenceNumber: number;
  readonly name: string;
  readonly description: string | null;

  /** Ngày cam kết ban đầu — chốt lúc khai, không sửa được. */
  readonly baselineDate: string;

  readonly plannedDate: string;
  readonly forecastDate: string | null;
  readonly actualDate: string | null;
  readonly isContractual: boolean;
  readonly isPaymentTrigger: boolean;
  readonly paymentPercent: number | null;
  readonly projectTaskId: string | null;
  readonly projectTaskName: string | null;
  readonly delayParty: ProjectMilestoneDelayPartyValue | null;
  readonly delayReason: string | null;
  readonly reachedAt: string | null;
  readonly reachedByUserId: string | null;
  readonly isReached: boolean;
  readonly slipDaysAgainstBaseline: number;
  readonly slipDaysAgainstPlan: number;
  readonly isOverdue: boolean;
}

/** Số ngày trượt quy về một bên chịu trách nhiệm. */
export interface ProjectMilestoneDelayDays {
  readonly party: ProjectMilestoneDelayPartyValue;
  readonly days: number;
}

/** Đường mốc của một dự án kèm bản tổng hợp. */
export interface ProjectMilestoneTimeline {
  readonly milestones: readonly ProjectMilestone[];
  readonly reachedCount: number;
  readonly overdueCount: number;
  readonly atRiskCount: number;
  readonly worstSlipDaysAgainstBaseline: number;
  readonly paymentPercentTotal: number;
  readonly paymentPercentReached: number;
  readonly delayDaysByParty: readonly ProjectMilestoneDelayDays[];
}

/** Nội dung khai hoặc sửa một mốc. */
export interface SaveProjectMilestonePayload {
  /** Bằng 0 nghĩa là để máy chủ đặt xuống cuối. */
  readonly sequenceNumber: number;

  readonly name: string;
  readonly description: string | null;

  /** Ngày cam kết. Chỉ dùng khi khai mốc mới; phép sửa bỏ qua. */
  readonly plannedDate: string;

  readonly isContractual: boolean;
  readonly isPaymentTrigger: boolean;
  readonly paymentPercent: number | null;
  readonly projectTaskId: string | null;
}

/** Nội dung dời lịch một mốc. */
export interface RescheduleProjectMilestonePayload {
  readonly plannedDate: string;
  readonly delayParty: ProjectMilestoneDelayPartyValue | null;
  readonly delayReason: string | null;
}

/** Nội dung đặt hoặc bỏ ngày dự báo. */
export interface ForecastProjectMilestonePayload {
  readonly forecastDate: string | null;
  readonly delayParty: ProjectMilestoneDelayPartyValue | null;
  readonly delayReason: string | null;
}

/** Nội dung ghi nhận một mốc đã đạt. */
export interface RecordProjectMilestonePayload {
  readonly actualDate: string;
  readonly delayParty: ProjectMilestoneDelayPartyValue | null;
  readonly delayReason: string | null;
}

/** Đọc đường mốc của một dự án. */
export async function getProjectMilestones(
  projectId: string,
): Promise<ProjectMilestoneTimeline> {
  return await request<ProjectMilestoneTimeline>(milestoneRoute(projectId));
}

/** Khai một mốc mới. */
export async function createProjectMilestone(
  projectId: string,
  payload: SaveProjectMilestonePayload,
): Promise<ProjectMilestone> {
  return await request<ProjectMilestone>(milestoneRoute(projectId), {
    method: 'POST',
    body: payload,
  });
}

/** Sửa phần khai báo của một mốc — không đụng tới ngày tháng. */
export async function updateProjectMilestone(
  projectId: string,
  milestoneId: string,
  payload: SaveProjectMilestonePayload,
): Promise<ProjectMilestone> {
  return await request<ProjectMilestone>(`${milestoneRoute(projectId)}/${milestoneId}`, {
    method: 'PUT',
    body: payload,
  });
}

/** Dời ngày kế hoạch của một mốc. */
export async function rescheduleProjectMilestone(
  projectId: string,
  milestoneId: string,
  payload: RescheduleProjectMilestonePayload,
): Promise<ProjectMilestone> {
  return await request<ProjectMilestone>(`${milestoneRoute(projectId)}/${milestoneId}/schedule`, {
    method: 'PUT',
    body: payload,
  });
}

/** Đặt hoặc bỏ ngày dự báo của một mốc. */
export async function forecastProjectMilestone(
  projectId: string,
  milestoneId: string,
  payload: ForecastProjectMilestonePayload,
): Promise<ProjectMilestone> {
  return await request<ProjectMilestone>(`${milestoneRoute(projectId)}/${milestoneId}/forecast`, {
    method: 'PUT',
    body: payload,
  });
}

/** Ghi nhận một mốc đã đạt. */
export async function recordProjectMilestone(
  projectId: string,
  milestoneId: string,
  payload: RecordProjectMilestonePayload,
): Promise<ProjectMilestone> {
  return await request<ProjectMilestone>(`${milestoneRoute(projectId)}/${milestoneId}/record`, {
    method: 'POST',
    body: payload,
  });
}

/** Mở lại một mốc đã ghi nhận đạt. */
export async function reopenProjectMilestone(
  projectId: string,
  milestoneId: string,
): Promise<ProjectMilestone> {
  return await request<ProjectMilestone>(`${milestoneRoute(projectId)}/${milestoneId}/reopen`, {
    method: 'POST',
  });
}

/** Xóa một mốc chưa đạt. */
export async function deleteProjectMilestone(
  projectId: string,
  milestoneId: string,
): Promise<void> {
  await requestNoContent(`${milestoneRoute(projectId)}/${milestoneId}`, { method: 'DELETE' });
}
